import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import requiredIf from 'react-required-if';

import { withStyles } from '@material-ui/core/styles';
import Input from '@material-ui/core/Input';
import Paper from '@material-ui/core/Paper';
import List from '@material-ui/core/List';

import { Scrollbars } from 'react-custom-scrollbars';

import Progress from 'components/common/Progress';
import IconButton from 'components/common/IconButton';
import IconArrowHeadBottom from 'components/icons/IconArrowHeadBottom';
import IconCross from 'components/icons/IconCross';
import AutosuggestFieldSuggestion from 'components/fields/AutosuggestField/AutosuggestFieldSuggestion';

import hexToRgb from 'styles/hexToRgb';
import throttle from 'utils/throttle';

const fontSize = '1.1vw';
const letterSpacing = '0.008em';
const lineHeight = '1.2';
const fontFamily = 'Times, "Times New Roman", serif';

const styles = theme => ({
  root: {
    position: 'absolute',
    zIndex: 10,
  },
  rootSuggestionsOpen: {
    zIndex: 200,
  },
  inputRoot: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    padding: 0,
    alignItems: 'flex-start',
  },
  inputInput: {
    position: 'absolute',
    top: 0,
    right: 0,
    left: 0,
    padding: 0,
    height: '100%',
    fontSize,
    lineHeight,
    letterSpacing,
    fontFamily,
    color: theme.palette.text.primary,
    overflow: 'hidden',
    resize: 'none',
    backgroundColor: 'transparent',
    zIndex: 1,
  },
  inputInputTextAlignRight: {
    textAlign: 'right',
  },
  inputInputTextAlignCenter: {
    textAlign: 'center',
  },
  fakeValue: {
    position: 'absolute',
    top: 0,
    left: 0,
    padding: 0,
    fontSize,
    lineHeight,
    letterSpacing,
    fontFamily,
    opacity: 0,
    pointerEvents: 'none',
    zIndex: -1,
  },
  fakeValueMultiline: {
    width: '100%',
    overflowWrap: 'break-word',
    wordWrap: 'breakWord',
  },
  inputBackground: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    padding: 0,
    backgroundColor: `rgba(${hexToRgb(theme.palette.grey[900])}, 0.05)`,
    opacity: 0.5,
  },
  suggestionsContainer: {
    position: 'absolute',
    left: 0,
    right: 0,
    minWidth: '100%',
    zIndex: 200,
    borderRadius: 4,
  },
  suggestionsContainerPlacementBottom: {
    top: '100%',
  },
  suggestionsContainerPlacementTop: {
    bottom: '100%',
  },
  suggestion: {
  },
  progress: {
    position: 'absolute',
    right: 4,
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 2,
  },
  iconArrow: {
    position: 'absolute',
    right: 8,
    top: '50%',
    transform: 'translateY(-50%)',
    width: 14,
    height: 14,
    fill: theme.palette.grey[600],
    pointerEvents: 'none',
    zIndex: 2,
  },
  iconArrowOpen: {
    transform: 'translateY(-50%) rotate(180deg)',
  },
  buttonClear: {
    position: 'absolute',
    right: -8,
    top: '50%',
    transform: 'translateY(-50%)',
    padding: 0,
    zIndex: 2,
  },
  iconCross: {
    width: 14,
    height: 14,
    fill: theme.palette.grey[600],
  },
});

const Textarea = ({ inputRef, ...otherProps }) => (
  <textarea
    {...otherProps}
    ref={inputRef}
  />
);

Textarea.propTypes = {
  inputRef: PropTypes.func.isRequired,
};

class DocumentPreviewFillAutosuggestField extends Component {
  constructor(props) {
    super(props);
    const initialValue = props.input.value != null ? props.input.value.toString() : '';
    this.state = {
      fakeValue: initialValue,
      selectionStart: null,
      threshold: 2,
      suggestions: [],
      renderSuggestions: props.alwaysRenderSuggestions,
      loadingSuggestions: false,
      offset: 0,
      selectedIndex: -1,
      focused: false,
      suggestionsSearchValue: initialValue,
    };

    this.justSelectedSuggestion = false;
    this.justClickedPaper = false;
    this.justClickedClearButton = false;
    this.justFocused = false;

    this.onChange = this.onChange.bind(this);
    this.onClearButtonClick = this.onClearButtonClick.bind(this);
    this.onSuggestionsFetchRequested =
      throttle(this.onSuggestionsFetchRequested.bind(this), 2000, true, true);
    this.onSuggestionsClearRequested = this.onSuggestionsClearRequested.bind(this);
    this.onScrollSuggestionsFetchRequested = this.onScrollSuggestionsFetchRequested.bind(this);
    this.onSuggestionClick = this.onSuggestionClick.bind(this);
    this.onPaperClick = this.onPaperClick.bind(this);
    this.onFocus = this.onFocus.bind(this);
    this.onBlur = this.onBlur.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
  }

  componentDidMount() {
    if (this.props.alwaysRenderSuggestions) {
      this.onSuggestionsFetchRequested();
    }
  }

  componentWillReceiveProps(nextProps) {
    const nextValue = nextProps.input.value != null ? nextProps.input.value.toString() : '';
    const currentValue = this.props.input.value != null ? this.props.input.value.toString() : '';
    if (nextValue !== currentValue
      && nextValue !== this.state.suggestionsSearchValue) {
      this.setState({
        fakeValue: nextValue,
        ...!this.justSelectedSuggestion ? {
          suggestionsSearchValue: nextValue,
        } : {},
      });
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { input, multiline, fetchSuggestions } = this.props;
    const { fakeValue, selectionStart, threshold, suggestionsSearchValue, offset } = this.state;

    if (fakeValue !== prevState.fakeValue
      && this.rootRef && this.fakeValueRef && this.inputRef) {
      if (multiline) {
        const rootHeight = this.rootRef.getBoundingClientRect().height;
        const fakeValueHeight = this.fakeValueRef.getBoundingClientRect().height;
        if (fakeValueHeight <= rootHeight) {
          input.onChange(fakeValue);
          this.inputRef.setSelectionRange(selectionStart, selectionStart);
        } else {
          const truncatedValue = fakeValue.slice(0, -1);
          // eslint-disable-next-line
          this.setState({
            fakeValue: truncatedValue,
            suggestionsSearchValue: truncatedValue,
          });
        }
      } else {
        const rootWidth = this.rootRef.getBoundingClientRect().width;
        const fakeValueWidth = this.fakeValueRef.getBoundingClientRect().width;
        if (fakeValueWidth + threshold < rootWidth) {
          input.onChange(fakeValue);
          this.inputRef.setSelectionRange(selectionStart, selectionStart);
        } else {
          const truncatedValue = fakeValue.slice(0, -1);
          // eslint-disable-next-line
          this.setState({
            fakeValue: truncatedValue,
            suggestionsSearchValue: truncatedValue,
          });
        }
      }
    }

    if (fetchSuggestions && !this.justSelectedSuggestion) {
      if (suggestionsSearchValue !== prevState.suggestionsSearchValue) {
        this.onSuggestionsFetchRequested();
      } else if (offset > 0 && offset !== prevState.offset) {
        this.onSuggestionsFetchRequested({ push: true });
      }
    }

    if (this.selectedSuggestionRef) {
      const { autoHeightMax } = this.props;
      const scrollTop = (this.selectedSuggestionRef.offsetTop
        + this.selectedSuggestionRef.getBoundingClientRect().height)
        - autoHeightMax;
      if (scrollTop >= 0 && this.scrollbarsRef) {
        this.scrollbarsRef.scrollTop(scrollTop);
      }
    }
  }

  componentWillUnmount() {
    clearTimeout(this.focusTimeout);
    clearTimeout(this.blurTimeout);
    clearTimeout(this.selectedTimeout);
    clearTimeout(this.clearTimeout);
  }

  onScrollSuggestionsFetchRequested() {
    const { limit } = this.props;
    const { suggestions, offset } = this.state;
    if ((suggestions.length - limit) >= offset) {
      this.setState({ offset: offset + limit });
    }
  }

  onSuggestionsFetchRequested(args = { push: false }) {
    const { limit, fetchSuggestions, ...otherProps } = this.props;
    const { suggestionsSearchValue } = this.state;
    if (fetchSuggestions && this.inputRef === document.activeElement) {
      this.setState({ loadingSuggestions: true });
      if (args.push) {
        const { suggestions, offset } = this.state;
        fetchSuggestions({ value: suggestionsSearchValue, limit, offset, ...otherProps })
          .then((newSuggestions) => {
            if (this.inputRef === document.activeElement) {
              this.setState({
                loadingSuggestions: false,
                renderSuggestions: true,
                suggestions: [
                  ...suggestions.slice(0, suggestions.length),
                  ...newSuggestions.slice(0, newSuggestions.length),
                ],
              });
            } else {
              this.setState({ loadingSuggestions: false });
            }
          });
      } else {
        const offset = 0;
        fetchSuggestions({ value: suggestionsSearchValue, limit, offset, ...otherProps })
          .then((suggestions) => {
            if (this.inputRef === document.activeElement) {
              if (this.scrollbarsRef) {
                this.scrollbarsRef.scrollTop(0);
              }
              this.setState({
                loadingSuggestions: false,
                renderSuggestions: true,
                suggestions,
                offset,
                selectedIndex: -1,
              });
            } else {
              this.setState({ loadingSuggestions: false });
            }
          });
      }
    }
  }

  onSuggestionsClearRequested() {
    const { alwaysRenderSuggestions } = this.props;
    if (!alwaysRenderSuggestions) {
      this.setState({
        suggestions: [],
      });
    }
  }

  onFocus(event) {
    const { input } = this.props;
    if (event.target === this.inputRef) {
      this.setState({ focused: true });
      if (!this.justSelectedSuggestion && !this.justClickedPaper) {
        this.onSuggestionsFetchRequested();
        this.setState({ renderSuggestions: true });
      }
      this.justFocused = true;
      this.focusTimeout = setTimeout(() => {
        this.justFocused = false;
      }, 200);
      if (input.onFocus) {
        input.onFocus(event);
      }
    }
  }

  onBlur(event) {
    const { input, alwaysRenderSuggestions } = this.props;
    if (event.target === this.inputRef) {
      this.setState({ focused: false });
      this.blurTimeout = setTimeout(() => {
        const position = this.inputRef
          ? this.inputRef.value.length
          : 0;
        if (!this.justSelectedSuggestion
          && !this.justClickedPaper
          && !this.justClickedClearButton
          && !this.justFocused) {
          if (!alwaysRenderSuggestions) {
            this.setState({ renderSuggestions: false });
            this.onSuggestionsClearRequested();
          }
          if (input.onBlur) {
            input.onBlur();
          }
        } else {
          if (this.inputRef) {
            this.inputRef.focus();
            this.inputRef.setSelectionRange(position, position);
          }
          this.justSelectedSuggestion = false;
          this.justClickedPaper = false;
          this.justClickedClearButton = false;
          this.justFocused = false;
        }
        this.blurTimeout = undefined;
      }, 200);
    }
  }

  onClearButtonClick() {
    const { input, disabled } = this.props;
    if (disabled) {
      return;
    }
    this.justClickedClearButton = true;
    input.onChange('');
    this.setState({
      fakeValue: '',
      selectionStart: 0,
      suggestionsSearchValue: '',
      renderSuggestions: true,
      selectedIndex: -1,
    });
    if (!this.blurTimeout && this.inputRef) {
      this.inputRef.focus();
    }
    this.onSuggestionsFetchRequested();
    this.clearTimeout = setTimeout(() => {
      this.justClickedClearButton = false;
    }, 200);
  }

  onKeyDown(event) {
    const {
      input: { value },
      onSuggestionSelected,
      fetchSuggestions,
      suggestions: staticSuggestions,
      change,
    } = this.props;
    const { selectedIndex, renderSuggestions, loadingSuggestions } = this.state;
    const suggestions = fetchSuggestions ? this.state.suggestions : staticSuggestions;
    if (renderSuggestions && suggestions && suggestions.length > 0) {
      switch (event.key) {
        case 'Enter': {
          if (suggestions[selectedIndex]) {
            event.preventDefault();
            this.applySuggestionValue(
              onSuggestionSelected(suggestions[selectedIndex], value, change),
            );
            this.setState({
              renderSuggestions: false,
              selectedIndex: -1,
            });
            this.justSelectedSuggestion = true;
            this.selectedTimeout = setTimeout(() => {
              this.justSelectedSuggestion = false;
            }, 200);
          }
          break;
        }
        case 'ArrowDown': {
          event.preventDefault();
          const newSelectedIndex = suggestions.length <= selectedIndex + 1
            ? 0 : selectedIndex + 1;
          this.applySuggestionValue(
            onSuggestionSelected(suggestions[newSelectedIndex], value, change),
          );
          this.setState({ selectedIndex: newSelectedIndex });
          this.justSelectedSuggestion = true;
          this.selectedTimeout = setTimeout(() => {
            this.justSelectedSuggestion = false;
          }, 200);
          if (newSelectedIndex === 0 && this.scrollbarsRef) {
            this.scrollbarsRef.scrollTop(0);
          }
          break;
        }
        case 'ArrowUp': {
          event.preventDefault();
          const newSelectedIndex = selectedIndex <= 0
            ? suggestions.length - 1 : selectedIndex - 1;
          this.applySuggestionValue(
            onSuggestionSelected(suggestions[newSelectedIndex], value, change),
          );
          this.setState({ selectedIndex: newSelectedIndex });
          this.justSelectedSuggestion = true;
          this.selectedTimeout = setTimeout(() => {
            this.justSelectedSuggestion = false;
          }, 200);
          break;
        }
        default:
      }
    } else if (event.key === 'ArrowDown' && !loadingSuggestions && fetchSuggestions) {
      this.onSuggestionsFetchRequested();
      this.setState({ renderSuggestions: true });
    }
  }

  onSuggestionClick(item) {
    const { input: { value }, onSuggestionSelected, change } = this.props;
    this.applySuggestionValue(onSuggestionSelected(item, value, change));
    this.setState({
      renderSuggestions: false,
      selectedIndex: -1,
    });
    this.justSelectedSuggestion = true;
    this.selectedTimeout = setTimeout(() => {
      this.justSelectedSuggestion = false;
    }, 200);
  }

  onPaperClick() {
    this.justClickedPaper = true;
  }

  applySuggestionValue(newValue) {
    const newValueStr = newValue != null ? newValue.toString() : '';
    this.setState({
      fakeValue: newValueStr,
      selectionStart: newValueStr.length,
      suggestionsSearchValue: newValueStr,
    });
  }

  onChange(event) {
    this.setState({
      fakeValue: event.target.value,
      selectionStart: event.target.selectionStart,
      suggestionsSearchValue: event.target.value,
    });
  }

  render() {
    const {
      input,
      x,
      y,
      width,
      height,
      type,
      maxLength,
      multiline,
      textAlign,
      documentWidth,
      documentPrintWidth,
      documentPrintHeight,
      fieldsShift,
      disabled,
      fetchSuggestions,
      renderSuggestionContent,
      suggestionsPlacement,
      autoHeightMax,
      classes,
    } = this.props;
    const {
      fakeValue,
      renderSuggestions,
      loadingSuggestions,
      selectedIndex,
      focused,
    } = this.state;
    const suggestions = fetchSuggestions
      ? this.state.suggestions
      : this.props.suggestions;
    const domRect = this.inputRef ? this.inputRef.getBoundingClientRect() : null;
    let placement = 'bottom';
    if (suggestionsPlacement) {
      placement = suggestionsPlacement;
    } else if (domRect) {
      if ((window.innerHeight - domRect.top - domRect.height) < autoHeightMax) {
        placement = 'top';
      }
    }
    let fakeValueLines;
    if (fakeValue.indexOf('\n') !== -1) {
      fakeValueLines = fakeValue.split('\n');
    }
    const showSuggestions = renderSuggestions
      && suggestions
      && suggestions.length > 0;
    return (
      <div
        className={classNames(classes.root, {
          [classes.rootSuggestionsOpen]: showSuggestions || focused,
        })}
        ref={(ref) => { this.rootRef = ref; }}
        style={{
          left: `${((x + fieldsShift.x) / documentPrintWidth) * 100}%`,
          top: `${((y + fieldsShift.y) / documentPrintHeight) * 100}%`,
          ...width ? { width: `${((width / documentPrintWidth) * 100)}%` } : {},
          ...height ? { height: `${(height / documentPrintHeight) * 100}%` } : {},
        }}
      >
        <Input
          classes={{
            root: classes.inputRoot,
            input: classNames(classes.inputInput, {
              [classes.inputInputTextAlignRight]: textAlign === 'right',
              [classes.inputInputTextAlignCenter]: textAlign === 'center',
            }),
          }}
          {...input}
          inputRef={(inputRef) => { this.inputRef = inputRef; }}
          onChange={this.onChange}
          onFocus={this.onFocus}
          onBlur={this.onBlur}
          onKeyDown={this.onKeyDown}
          type={type}
          disableUnderline
          inputProps={{
            maxLength,
            autoComplete: 'off',
            style: {
              top: (2 / 2.834646) * (documentWidth / documentPrintWidth),
              fontSize: (10 / 2.834646) * (documentWidth / documentPrintWidth),
            },
            disabled,
          }}
          {...multiline ? { inputComponent: Textarea } : {}}
        />
        {loadingSuggestions && (
          <Progress
            size={22}
            className={classes.progress}
          />
        )}
        {!loadingSuggestions && (
          input.value && input.value.toString().length > 0 ? (
            <IconButton
              className={classes.buttonClear}
              onClick={this.onClearButtonClick}
              onMouseDown={(event) => {
                event.preventDefault();
              }}
              tooltipContent={!disabled ? 'Clear Value' : null}
              disabled={disabled}
              tabIndex={-1}
            >
              <IconCross className={classes.iconCross} />
            </IconButton>
          ) : (
            <IconArrowHeadBottom
              className={classNames(classes.iconArrow, {
                [classes.iconArrowOpen]: showSuggestions,
              })}
            />
          )
        )}
        <div
          className={classes.inputBackground}
          style={{
            top: (3 / 2.834646) * (documentWidth / documentPrintWidth),
          }}
        />
        <div
          className={classNames(classes.fakeValue, {
            [classes.fakeValueMultiline]: multiline,
          })}
          ref={(ref) => { this.fakeValueRef = ref; }}
          style={{
            top: (3 / 2.834646) * (documentWidth / documentPrintWidth),
            fontSize: (10 / 2.834646) * (documentWidth / documentPrintWidth),
          }}
        >
          {fakeValueLines ? (
            fakeValueLines.map((line, index) => (
              // eslint-disable-next-line
              <div key={index}>
                {line.length > 0 ? line.replace(/ {2}/g, '\u00a0 ') : '.'}
              </div>
            ))
          ) : (
            fakeValue.replace(/ {2}/g, '\u00a0 ')
          )}
        </div>
        {showSuggestions &&
          <Paper
            onClick={this.onPaperClick}
            className={classNames(classes.suggestionsContainer, {
              [classes.suggestionsContainerPlacementBottom]: placement === 'bottom',
              [classes.suggestionsContainerPlacementTop]: placement === 'top',
            })}
          >
            <Scrollbars
              autoHeight
              autoHeightMin={0}
              autoHeightMax={autoHeightMax}
              thumbMinSize={30}
              universal
              ref={(scrollbarsRef) => {
                this.scrollbarsRef = scrollbarsRef;
              }}
              onScrollFrame={(event) => {
                if (event.top > 0.8) {
                  this.onScrollSuggestionsFetchRequested();
                }
              }}
              renderView={props => (
                <div {...props} tabIndex="-1" />
              )}
            >
              <List disablePadding>
                {suggestions.map((item, index) => (
                  <AutosuggestFieldSuggestion
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                    onClick={() => this.onSuggestionClick(item)}
                    classes={{
                      root: classes.suggestion,
                    }}
                    {...index === selectedIndex ? {
                      selected: true,
                      rootRef: (selectedSuggestionRef) => {
                        this.selectedSuggestionRef = selectedSuggestionRef;
                      },
                    } : {}}
                  >
                    {renderSuggestionContent(item)}
                  </AutosuggestFieldSuggestion>
                ))}
              </List>
            </Scrollbars>
          </Paper>
        }
      </div>
    );
  }
}

DocumentPreviewFillAutosuggestField.propTypes = {
  input: PropTypes.shape({
    name: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]).isRequired,
    onChange: PropTypes.func.isRequired,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
  }).isRequired,
  x: PropTypes.number.isRequired,
  y: PropTypes.number.isRequired,
  width: PropTypes.number.isRequired,
  height: PropTypes.number,
  type: PropTypes.string.isRequired,
  maxLength: PropTypes.number,
  textAlign: PropTypes.oneOf(['left', 'right', 'center']),
  multiline: PropTypes.bool,
  documentWidth: PropTypes.number.isRequired,
  documentPrintWidth: PropTypes.number.isRequired,
  documentPrintHeight: PropTypes.number.isRequired,
  fieldsShift: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number,
  }),
  disabled: PropTypes.bool,
  fetchSuggestions: requiredIf(PropTypes.func, props => !props.suggestions),
  suggestions: requiredIf(PropTypes.arrayOf(PropTypes.object), props => !props.fetchSuggestions),
  onSuggestionSelected: PropTypes.func,
  renderSuggestionContent: PropTypes.func,
  alwaysRenderSuggestions: PropTypes.bool,
  limit: PropTypes.number,
  autoHeightMax: PropTypes.number,
  suggestionsPlacement: PropTypes.oneOf(['top', 'bottom']),
  classes: PropTypes.shape({
    root: PropTypes.string.isRequired,
    rootSuggestionsOpen: PropTypes.string.isRequired,
    inputRoot: PropTypes.string.isRequired,
    inputInput: PropTypes.string.isRequired,
    inputInputTextAlignRight: PropTypes.string.isRequired,
    inputInputTextAlignCenter: PropTypes.string.isRequired,
    fakeValue: PropTypes.string.isRequired,
    fakeValueMultiline: PropTypes.string.isRequired,
    inputBackground: PropTypes.string.isRequired,
    suggestionsContainer: PropTypes.string.isRequired,
    suggestionsContainerPlacementBottom: PropTypes.string.isRequired,
    suggestionsContainerPlacementTop: PropTypes.string.isRequired,
    suggestion: PropTypes.string.isRequired,
    progress: PropTypes.string.isRequired,
    iconArrow: PropTypes.string.isRequired,
    iconArrowOpen: PropTypes.string.isRequired,
    buttonClear: PropTypes.string.isRequired,
    iconCross: PropTypes.string.isRequired,
  }).isRequired,
};

DocumentPreviewFillAutosuggestField.defaultProps = {
  maxLength: null,
  multiline: false,
  height: 4,
  textAlign: 'left',
  fieldsShift: { x: 0, y: 0 },
  disabled: false,
  renderSuggestionContent: suggestion => suggestion,
  onSuggestionSelected: suggestion => suggestion,
  alwaysRenderSuggestions: false,
  limit: 10,
  autoHeightMax: 200,
  fetchSuggestions: null,
  suggestions: null,
  suggestionsPlacement: null,
};

export default withStyles(styles)(DocumentPreviewFillAutosuggestField);



// WEBPACK FOOTER //
// ./src/components/common/DocumentPreviewFillForm/DocumentPreviewFillAutosuggestField.js